import { useState } from "react";
import { useNavigate } from "react-router-dom";
import BillSummary, { calcAmount } from "./bill-summary";
import PaymentMethods from "./payment-methods";
import { createOrder } from "features/delivery/services/orderApi";
import type { CreateOrderBody } from "types/api.generated";
import type { PaymentMethod } from "types/delivery.types";

type DeliveryFormData = Omit<CreateOrderBody, "paymentMethod" | "couponCode">;

interface Props {
  orderFormData: DeliveryFormData;
  onBack: () => void;
}

const Payment = ({ orderFormData, onBack }: Props) => {
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("cash");
  const [coupon, setCoupon] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const packageWeight = orderFormData.packageWeight
    ? Number(orderFormData.packageWeight)
    : undefined;
  const amount = calcAmount(packageWeight);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      if (orderFormData.pickupLat == null || orderFormData.pickupLng == null) {
        setError("Pickup location is required. Go back and select an address from suggestions.");
        setIsSubmitting(false);
        return;
      }
      const order = await createOrder({
        ...orderFormData,
        packageWeight,
        paymentMethod,
        couponCode: coupon || undefined,
        vehicleTypeOverride: orderFormData.vehicleTypeOverride || undefined,
      });

      navigate(`/customer/orders/${order.id}`);
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : "Failed to place order. Please try again.";
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Order summary + promo code */}
      <div className="fade-up bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-4">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span
              className="material-symbols-outlined text-primary text-[20px]"
              style={{ fontVariationSettings: '"FILL" 1' }}
            >
              receipt
            </span>
          </div>
          <h3 className="font-black text-base text-slate-800">Order Summary</h3>
        </div>
        <BillSummary packageWeight={packageWeight} />
        {/* Promo code — inline below price breakdown */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">
            Promo Code
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value)}
              placeholder="Enter promo code"
              className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 focus:bg-white transition-all placeholder:text-slate-400"
            />
            <button
              type="button"
              className="px-4 py-2.5 rounded-xl border-2 border-primary text-primary text-sm font-extrabold hover:bg-primary hover:text-white transition-all"
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Payment method */}
      <div
        className="fade-up bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-4"
        data-delay="0.05"
      >
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span
              className="material-symbols-outlined text-primary text-[20px]"
              style={{ fontVariationSettings: '"FILL" 1' }}
            >
              credit_card
            </span>
          </div>
          <h3 className="font-black text-base text-slate-800">
            Payment Method
          </h3>
        </div>
        <PaymentMethods selected={paymentMethod} onChange={setPaymentMethod} />
      </div>

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4 mb-4">
          <span className="material-symbols-outlined text-red-500 text-[20px] flex-shrink-0">
            error
          </span>
          <p className="text-sm font-semibold text-red-600">{error}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 px-5 py-4 rounded-2xl border-2 border-slate-200 text-slate-600 font-bold text-sm hover:border-primary hover:text-primary transition-all"
        >
          <span className="material-symbols-outlined text-[18px]">
            arrow_back
          </span>
          Back
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 bg-primary text-white font-extrabold py-4 rounded-2xl text-base hover:shadow-xl hover:shadow-primary/25 hover:scale-[1.02] transition-all active:scale-95 flex items-center justify-center gap-2 relative overflow-hidden group disabled:opacity-60 disabled:scale-100 disabled:shadow-none"
        >
          <span className="absolute inset-0 bg-primary-deep scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 rounded-2xl" />
          {isSubmitting ? (
            <svg
              className="animate-spin h-5 w-5 relative z-10"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
          ) : (
            <>
              <span className="relative z-10">
                Pay &#8358;{amount.toLocaleString("en-NG")}
              </span>
              <span
                className="material-symbols-outlined relative z-10 text-[20px]"
                style={{ fontVariationSettings: '"FILL" 1' }}
              >
                lock
              </span>
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default Payment;
